import { CreditFacilityEventLayout } from "features/credit-facility/CreditFacilityEventLayout";
import { CreditFacilitySubpageProps } from "features/credit-facility/CreditFacilitySubpageProps";
import { useCreditFacilityReallocationPrincipalTranchesRetrieveSuspense } from "gen/endpoints/credit-facility/credit-facility";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import { PageWizardFormFooter } from "shared/components/layout/PageWizard";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { getSelectedChildrenRows } from "shared/components/table/data/DataTableUtils";
import { useToast } from "shared/components/toast/useToast";
import { rootRoute } from "shared/navigation/Routes";

export const CreditFacilityReallocationSelectTranches: React.FC = () => {
  const { t } = useTranslation(["creditFacility", "common"]);
  const navigate = useNavigate();

  const { fundFamilyId, creditFacilityId, fundFamilyEvent } =
    useOutletContext<CreditFacilitySubpageProps>();

  const { data: tranchesTable } =
    useCreditFacilityReallocationPrincipalTranchesRetrieveSuspense(
      fundFamilyId,
      creditFacilityId,
      fundFamilyEvent.id!,
    );

  const splitRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .creditFacilities({})
    .creditFacility({ creditFacilityId })
    .event({ fundFamilyEventId: fundFamilyEvent.id! })
    .reallocation({})
    .split({}).$;

  // this is all of the entities and their selected state
  const [rowSelectionState] = useState<Record<string, boolean>>(
    getSelectedChildrenRows(tranchesTable.rows),
  );

  // this is only the selected entities
  const [selectedState, setSelectedState] = useState<Record<string, boolean>>(
    Object.keys(rowSelectionState).reduce(
      (selectedState, id: string) => ({
        ...selectedState,
        ...(rowSelectionState[id] ? { [id]: true } : {}),
      }),
      {},
    ),
  );
  const { showErrorToast } = useToast();
  const onClickNext = () => {
    if (Object.keys(selectedState).length === 0) {
      showErrorToast(
        t("creditFacility:reallocation.selectTranches.noTranchesSelected"),
      );
      return;
    }
    const ids = encodeURIComponent(Object.keys(selectedState).join(","));
    const route = ids ? `${splitRoute}?ids=${ids}` : splitRoute;
    navigate(route);
  };

  return (
    <CreditFacilityEventLayout canEditEvent={true}>
      <TableBox>
        <TableBoxHeader>
          <TableBoxTitle>
            {t("creditFacility:reallocation.selectTranches.principalTranches")}
          </TableBoxTitle>
          <DataTableBoxActions />
        </TableBoxHeader>
        <TableBoxBody>
          <DataTable
            data={tranchesTable}
            rowSelection={rowSelectionState}
            onRowSelectionChange={setSelectedState}
          />
        </TableBoxBody>
      </TableBox>
      <PageWizardFormFooter onClickNext={onClickNext} />
    </CreditFacilityEventLayout>
  );
};
